import React, { useEffect, useState } from "react";
import "./App.css";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/jetbrains-mono/400.css";
import { personalInfo } from "./mockData";
import HeroAboutCompetencies from "./components/portfolio/HeroAboutCompetencies";
import DomainExpertise from "./components/portfolio/DomainExpertise";
import ExperienceSkillsContact from "./components/portfolio/ExperienceSkillsContact";
import { Menu, X, Mail, Linkedin, MapPin, Phone, ArrowUp, Github, BookOpen } from "lucide-react";

const navLinks = [
  { label: "About", href: "#about" },
  { label: "Competencies", href: "#competencies" },
  { label: "Domains", href: "#domains" },
  { label: "Experience", href: "#experience" },
  { label: "Projects", href: "#projects" },
  { label: "Skills", href: "#skills" },
  { label: "Education", href: "#education" },
  { label: "Publications", href: "#publications" },
  { label: "Contact", href: "#contact" }
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0a0f1c]/95 backdrop-blur-md border-b border-slate-800/50 shadow-lg"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <a
          href="#hero"
          onClick={(e) => handleNavClick(e, "#hero")}
          className="font-mono text-cyan-400 text-lg font-semibold tracking-tight hover:text-cyan-300 transition-colors"
        >
          AG<span className="text-slate-500">.</span>
        </a>
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className="text-sm text-slate-400 hover:text-cyan-400 transition-colors duration-200 font-medium animated-underline"
            >
              {link.label}
            </a>
          ))}
        </nav>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden text-slate-400 hover:text-cyan-400 transition-colors"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      {mobileOpen && (
        <div className="md:hidden bg-[#0a0f1c]/98 backdrop-blur-lg border-t border-slate-800/50">
          <nav className="flex flex-col px-6 py-4 gap-4">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-sm text-slate-400 hover:text-cyan-400 transition-colors font-medium py-2"
              >
                {link.label}
              </a>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

const Footer = () => (
  <footer className="bg-[#060a14] border-t border-slate-800/50 py-12">
    <div className="max-w-7xl mx-auto px-6">
      <div className="flex flex-col md:flex-row justify-between items-center gap-6">
        <div className="flex flex-col items-center md:items-start gap-2">
          <span className="font-mono text-cyan-400 text-lg font-semibold">
            AG<span className="text-slate-500">.</span>
          </span>
          <p className="text-slate-500 text-sm">
            Senior Engineering Manager | Healthcare Technology Innovator
          </p>
        </div>
        <div className="flex items-center gap-6">
          <a
            href={`mailto:${personalInfo.contact.email}`}
            className="text-slate-500 hover:text-cyan-400 transition-colors"
            aria-label="Email"
          >
            <Mail size={18} />
          </a>
          <a
            href={personalInfo.contact.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="text-slate-500 hover:text-cyan-400 transition-colors"
            aria-label="LinkedIn"
          >
            <Linkedin size={18} />
          </a>
          <a
            href={personalInfo.contact.github}
            target="_blank"
            rel="noopener noreferrer"
            className="text-slate-500 hover:text-cyan-400 transition-colors"
            aria-label="GitHub"
          >
            <Github size={18} />
          </a>
          <a
            href={personalInfo.contact.medium}
            target="_blank"
            rel="noopener noreferrer"
            className="text-slate-500 hover:text-cyan-400 transition-colors"
            aria-label="Medium"
          >
            <BookOpen size={18} />
          </a>
          <a
            href={`tel:${personalInfo.contact.phone}`}
            className="text-slate-500 hover:text-cyan-400 transition-colors"
            aria-label="Phone"
          >
            <Phone size={18} />
          </a>
          <span className="text-slate-600 flex items-center gap-1 text-sm">
            <MapPin size={14} /> {personalInfo.contact.location}
          </span>
        </div>
      </div>
      <div className="mt-8 pt-6 border-t border-slate-800/30 text-center">
        <p className="text-slate-600 text-xs">
          &copy; {new Date().getFullYear()} Abraham Gogulamudi. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

const ScrollToTop = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const handleScroll = () => setVisible(window.scrollY > 500);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  if (!visible) return null;
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className="fixed bottom-8 right-8 z-40 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/30 text-cyan-400 p-3 rounded-full transition-all duration-300 hover:scale-110 backdrop-blur-sm"
    >
      <ArrowUp size={20} />
    </button>
  );
};

function App() {
  return (
    <div className="bg-[#0a0f1c] min-h-screen text-slate-100">
      <Header />
      <main>
        <HeroAboutCompetencies />
        <DomainExpertise />
        <ExperienceSkillsContact />
      </main>
      <Footer />
      <ScrollToTop />
    </div>
  );
}

export default App;
